import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { products, categories } from '../data/mock';
import { ChevronDown, SlidersHorizontal, Check } from 'lucide-react';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState('featured');

  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => {
    document.title = 'Shop Smart Electronics & Everyday Tech | Horizon Techline';
  }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (selectedCategory) {
      result = result.filter(p => p.categorySlug === selectedCategory);
    }
    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'name': result.sort((a, b) => a.name.localeCompare(b.name)); break;
      default: break;
    }
    return result;
  }, [selectedCategory, sortBy]);

  const handleCategoryChange = (categorySlug) => {
    if (categorySlug) setSearchParams({ category: categorySlug });
    else setSearchParams({});
  };

  const currentCategory = categories.find(c => c.slug === selectedCategory);

  const CatButton = ({ active, onClick, label, count }) => (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm transition-colors ${active ? 'bg-athletic-600 text-white font-medium' : 'text-dark-600 hover:bg-dark-50'}`}
    >
      <span className="flex items-center gap-2">{active && <Check className="w-3.5 h-3.5" />}{label}</span>
      <span className={active ? 'text-white/70' : 'text-dark-400'}>{count}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-white text-dark-800">
      <Header />
      <CartDrawer />

      <main>
        {/* Calm light page header */}
        <section className="bg-dark-50/70 border-b border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <nav className="text-sm text-dark-400 mb-3">
              <Link to="/" className="hover:text-athletic-700">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-dark-700">{currentCategory ? currentCategory.name : 'Shop all'}</span>
            </nav>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-dark-900">
              {currentCategory ? currentCategory.name : 'Shop all products'}
            </h1>
            <p className="mt-3 text-dark-500 max-w-2xl">
              {currentCategory
                ? currentCategory.description
                : 'A tightly curated collection of everyday electronics \u2014 audio, gaming, creator gear and more.'}
            </p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid lg:grid-cols-[240px_1fr] gap-10">
            {/* Sidebar */}
            <aside className="lg:sticky lg:top-28 self-start">
              <div className="flex items-center gap-2 mb-3 text-dark-900 font-semibold text-sm">
                <SlidersHorizontal className="w-4 h-4 text-athletic-600" /> Categories
              </div>
              <div className="space-y-1">
                <CatButton active={!selectedCategory} onClick={() => handleCategoryChange('')} label="All products" count={products.length} />
                {categories.map((cat) => (
                  <CatButton
                    key={cat.id}
                    active={selectedCategory === cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    label={cat.name}
                    count={cat.productCount}
                  />
                ))}
              </div>
            </aside>

            {/* Grid column */}
            <div>
              <div className="flex items-center justify-between gap-3 mb-6">
                <p className="text-sm text-dark-600">
                  <span className="font-semibold text-dark-900">{filteredProducts.length}</span> product{filteredProducts.length !== 1 ? 's' : ''}
                </p>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-white border border-dark-200 text-dark-700 text-sm rounded-full px-4 py-2.5 pr-9 focus:outline-none focus:ring-2 focus:ring-athletic-500 cursor-pointer"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="name">Name: A-Z</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400 pointer-events-none" />
                </div>
              </div>

              {filteredProducts.length === 0 ? (
                <div className="bg-dark-50 rounded-2xl p-12 text-center border border-dark-100">
                  <p className="text-dark-600">No products found in this category.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShopPage;
